import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, MessageCircle } from "lucide-react";
import { Toaster, toast } from "sonner";
import { CLINIC, LOGO_SVG, doctorDisplay, buildWhatsAppBrandedMessage, doctorPhotoUrl } from "../lib/clinic";
import { shareEducationArticle } from "../lib/educationSharing";
import { languageDirection, patientLanguageName } from "../lib/patientLanguages";

/**
 * Branded, WhatsApp-shareable PDF view of an education article.
 * Layout mirrors PrintDietPlan / PrintExercisePlan for visual consistency.
 * `article_raw` is used so the print header/footer aren't duplicated.
 */
export default function PrintEducationArticle() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/education/articles/${aid}`);
        setDoc(r.data.article);
      } catch (e) {
        console.warn("load article failed:", e);
        toast.error("Could not load education article");
        setError("Could not load education article");
      }
    })();
  }, [aid]);

  if (!doc) return <div data-testid="edu-print-load-status" role={error ? "alert" : "status"} className="p-8 text-sm text-slate-500">{error || "Loading article…"}</div>;
  const isMr = doc.language === "mr";
  const date = new Date(doc.issued_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const bodyText = doc.article_raw || doc.article || "";

  const shareWA = async () => {
    if (!bodyText) return;
    setSharing(true);
    try { await shareEducationArticle(doc); }
    catch (e) { toast.error(e.response?.data?.detail || e.message || "Could not share article"); }
    finally { setSharing(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 print-shell">
      <Toaster position="top-right" richColors />
      <div className="print-toolbar sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2 flex flex-wrap items-center gap-2 no-print">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="edu-print-back"><ArrowLeft size={14} className="mr-1" /> Back</Button>
        <div data-testid="edu-print-language" className="text-sm text-slate-600 break-words">{patientLanguageName(doc.language)} · {doc.topic}</div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={shareWA} disabled={sharing} className="border-emerald-300 text-emerald-700" data-testid="edu-print-wa-btn">
            <MessageCircle size={14} className="mr-1" /> WhatsApp
          </Button>
          <Button size="sm" onClick={() => window.print()} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="edu-print-btn">
            <Printer size={14} className="mr-1" /> Print / Save PDF
          </Button>
        </div>
      </div>

      <div className="max-w-[820px] mx-auto my-6 bg-white border border-slate-200 shadow-sm print-page" data-testid="edu-print-page">
        <div className="p-4 sm:p-8">
          <header className="flex flex-wrap gap-4 justify-between items-start pb-4 border-b-2 border-slate-900">
            <div className="flex flex-wrap gap-4 items-start min-w-0">
              <div className="w-16 h-20 flex-shrink-0">{LOGO_SVG}</div>
              <img src={doctorPhotoUrl()} alt={CLINIC.doctorName} className="h-16 w-16 rounded-full object-cover object-top border-2 border-pink-600 flex-shrink-0" data-testid="edu-print-doctor-photo" />
              <div>
                <div className="font-heading text-xl font-bold text-slate-900 leading-tight">{CLINIC.name}</div>
                <div className="text-xs text-slate-700 mt-1 font-medium">{doctorDisplay(CLINIC.doctorName)}</div>
                <div className="text-[10px] text-slate-500 leading-snug">{CLINIC.qualifications}</div>
                <div className="text-[10px] text-slate-500">{CLINIC.regNo}</div>
                <div className="text-[10px] font-semibold text-pink-700 mt-0.5">{CLINIC.specialties}</div>
              </div>
            </div>
            <div className="text-right text-[10px] text-slate-600 max-w-[220px] leading-snug">
              <div>{CLINIC.address}</div>
              {(CLINIC.phones || []).length > 0 && <div className="mt-1 tabular">{CLINIC.phones.join(" · ")}</div>}
            </div>
          </header>

          <section className="mt-4 py-3 border-b border-slate-200 text-center">
            <div className="uppercase text-[10px] tracking-[0.25em] text-pink-600 font-semibold">
              {isMr ? "रुग्ण शिक्षण लेख" : "Patient Education Article"}
            </div>
            <div className="font-heading text-2xl font-bold text-slate-900 mt-0.5">{doc.topic}</div>
            <div className="text-[10px] text-slate-500 mt-1 tabular">
              {isMr ? "दिनांक" : "Date"}: {date} · {isMr ? "लेख क्र." : "Article No."} {doc.article_id}
            </div>
          </section>

          <section className="mt-6">
            <pre lang={doc.language} dir={languageDirection(doc.language)} className="whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-slate-800 font-sans" data-testid="edu-print-body">{bodyText}</pre>
          </section>

          <section className="py-3 border-t border-slate-200 mt-8 flex justify-between items-end rx-signature-block">
            <div className="text-[11px] text-slate-500">
              <div className="uppercase text-[9px] tracking-wider text-slate-500 font-semibold">
                {isMr ? "लेख क्र." : "Article No."}
              </div>
              <div className="tabular font-medium text-slate-800">{doc.article_id}</div>
            </div>
            <div className="text-right max-w-[62%]">
              <div className="border-t border-slate-800 pt-1 mt-8 text-[11px]">
                <div className="font-medium">{doctorDisplay(CLINIC.doctorName)}</div>
                <div className="text-[9.5px] text-slate-600 leading-snug mt-0.5">{CLINIC.qualifications}</div>
              </div>
            </div>
          </section>

          {["en", "mr"].includes(doc.language) && <footer className="mt-4 pt-3 border-t border-slate-200 text-[9px] text-slate-500 leading-relaxed print-doctor-footer">
            {isMr
              ? <>ही माहिती <strong>{doctorDisplay(CLINIC.doctorName)}, ({CLINIC.qualifications})</strong> यांच्या क्लिनिकल पर्यवेक्षणाखाली <strong>{CLINIC.name}</strong> कडून प्रकाशित केली आहे. वैयक्तिक सल्ल्यासाठी कृपया क्लिनिकशी संपर्क साधा.</>
              : <>This information is published under the clinical supervision of <strong>{doctorDisplay(CLINIC.doctorName)}, ({CLINIC.qualifications})</strong>, {CLINIC.name}. Please contact the clinic for personalised guidance.</>
            }
          </footer>}
        </div>

        {["en", "mr"].includes(doc.language) && <div className="print-marketing-footer border-t-2 border-pink-600 bg-gradient-to-r from-pink-50 via-white to-pink-50 text-slate-700">
          <div className="grid grid-cols-3 gap-3 px-4 py-2">
            <div>
              <div className="uppercase text-[9px] tracking-[0.25em] text-pink-600 font-semibold">{isMr ? "पुराव्यावर आधारित" : "Evidence-Based"}</div>
              <div className="text-[10px] mt-0.5 leading-snug">{isMr ? "ADA / ICMR / WHO मार्गदर्शक तत्त्वांवर आधारित रुग्ण-अनुकूल माहिती." : "Guideline-based patient education (ADA / ICMR / WHO)."}</div>
            </div>
            <div>
              <div className="uppercase text-[9px] tracking-[0.25em] text-pink-600 font-semibold">{isMr ? "नियमित पाठपुरावा" : "Follow-up Matters"}</div>
              <div className="text-[10px] mt-0.5 leading-snug">{isMr ? "नियमित तपासणीसाठी क्लिनिकला भेट द्या. दुर्लक्ष टाळा." : "Book a follow-up review as advised. Do not skip check-ups."}</div>
            </div>
            <div>
              <div className="uppercase text-[9px] tracking-[0.25em] text-pink-600 font-semibold">{isMr ? "संपर्क" : "Contact"}</div>
              <div className="text-[10px] mt-0.5 leading-snug">{CLINIC.phones?.join(" · ")}</div>
            </div>
          </div>
        </div>}
      </div>
    </div>
  );
}
